import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Row, Col, Table, Input, Label } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import { CheckFecha } from "./CheckFecha";
import { CampoAuto } from "./CampoAuto";
import { useAutoGuardado, fondoFila, SELLO_EN, COLOR_SELLO } from "./autoGuardado";
import { FotoArticulo } from "../../../FotoArticulo";
import { rangoTalla } from "../../../desglosePedido";

// Supplier (China) production tracking — always in English (per client request)
// Fields left empty show (in blue) what the system already knows: item no., load
// date, cost, retail price, container and status come from the customer order,
// the PO and the shipments. Whatever is typed wins.
const CAMPOS = [
  { key: "itemNo", label: "Item No.", w: 130 },
  { key: "deliveryDate", label: "Delivery / Load Date", w: 130, tipo: "date" },
  { key: "colorCode", label: "Color Code", w: 100 },
  { key: "costoRmb", label: "Cost RMB / USD", w: 120 },
  { key: "pdv", label: "Retail price", w: 100 },
  { key: "labDips", label: "Lab Dips", w: 95, tipo: "check" },
  { key: "ppSample", label: "P/P Sample", w: 95, tipo: "check" },
  { key: "aprobacionProduccion", label: "Production approval", w: 95, tipo: "check" },
  { key: "fitComment", label: "Fit Comment", w: 130 },
  { key: "labelStatus", label: "Label", w: 95, tipo: "check" },
  { key: "tagSticker", label: "Tag / Sticker", w: 95, tipo: "check" },
  { key: "fangQuestion", label: "Fang / Question", w: 120 },
  { key: "shippingSample", label: "Shipping Sample", w: 95, tipo: "check" },
  { key: "paqueteria", label: "Courier / Tracking No.", w: 150 },
  { key: "container", label: "Container", w: 100 },
  { key: "statusSeg", label: "Status", w: 100 },
];

function SeguimientoChina() {
  const { user } = useContext(AuthContext);
  const URL_ORDENES_COMPRA = process.env.REACT_APP_URL_ORDENES_COMPRA;
  const URL_ARTICULOS_OC = process.env.REACT_APP_URL_ARTICULOS_OC;
  const URL_SEGUIMIENTOS = process.env.REACT_APP_URL_SEGUIMIENTOS;
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const [ordenes, setOrdenes] = useState([]);
  const [selectedOC, setSelectedOC] = useState("");
  // El proveedor revisa TODAS sus ordenes de una sola vez, no de una en una.
  const [selectedProveedor, setSelectedProveedor] = useState("");
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  // modelos de cada PO (para que el selector diga que trae cada orden)
  const [modelosPorOC, setModelosPorOC] = useState({});
  // what the system fills in by itself, per PO line
  const [autos, setAutos] = useState({});

  // Cada renglon se guarda solo; no hay boton de guardar. Los avisos van en ingles.
  const { programa, estado } = useAutoGuardado(
    (fila) => {
      const body = {};
      CAMPOS.forEach((c) => { body[c.key] = fila[c.key] === "" ? "NA" : String(fila[c.key]); });
      return axios.patch(`${URL_ARTICULOS_OC}/${fila._id}`, body, { headers });
    },
    { tituloError: "Not saved", textoError: "Check your connection" }
  );

  useEffect(() => {
    axios.get(URL_ORDENES_COMPRA, { headers })
      .then((res) => setOrdenes((res.data || []).filter((a) => a.is_active === "Si")))
      .catch(() => Swal.fire({ icon: "error", title: "Oops...", text: "Could not load purchase orders" }));
    axios.get(`${URL_SEGUIMIENTOS}Automaticos`, { headers })
      .then((res) => setAutos(res.data || {}))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Arma la tabla con los renglones de una o de varias ordenes.
  function pinta(lista, ocsPorId) {
    // ordenadas por PO, modelo, color y talla: asi las fotos van una vez por modelo
    const refDe = (a) => { const r = Array.isArray(a.ordenesCompra) ? a.ordenesCompra[0] : a.ordenesCompra; return String((r && r._id) || r); };
    const orden = [...lista].sort((x, y) => {
      const px = ocsPorId[refDe(x)]?.idOrdenCompra || 0, py = ocsPorId[refDe(y)]?.idOrdenCompra || 0;
      if (px !== py) return py - px;
      const mx = x.articulos?.[0]?.codigo || "", my = y.articulos?.[0]?.codigo || "";
      if (mx !== my) return mx > my ? 1 : -1;
      if ((x.renglon || 1) !== (y.renglon || 1)) return (x.renglon || 1) - (y.renglon || 1);
      const cx = x.colores?.[0]?.name || "", cy = y.colores?.[0]?.name || "";
      if (cx !== cy) return cx > cy ? 1 : -1;
      return rangoTalla(x.tallas?.[0]?.name) - rangoTalla(y.tallas?.[0]?.name);
    });
    const porOC = {};
    orden.forEach((a) => {
      const k = refDe(a);
      porOC[k] = porOC[k] || new Set();
      if (a.articulos?.[0]?.codigo) porOC[k].add(a.articulos[0].codigo);
    });
    setModelosPorOC((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(porOC).map(([k, v]) => [k, [...v]])) }));
    let anterior = "";
    setFilas(orden.map((a, i) => {
      // ordenesCompra es arreglo de refs, como casi todo en este sistema
      const refOC = Array.isArray(a.ordenesCompra) ? a.ordenesCompra[0] : a.ordenesCompra;
      const oc = ocsPorId[(refOC && refOC._id) || refOC] || {};
      const base = {
              _id: a._id,
              no: i + 1,
              po: oc.idOrdenCompra || "",
              articulo: a.articulos?.[0] ? `${a.articulos?.[0]?.codigo || ""} ${a.articulos?.[0]?.nombre || ""}`.trim() : "",
              idArticulo: a.articulos?.[0]?._id || "",
              urlFoto: a.articulos?.[0]?.urlFoto || "",
              urlFotoFit: a.articulos?.[0]?.urlFotoFit || "",
              color: a.colores?.[0] ? `${a.colores?.[0]?.name}` : "",
              talla: a.tallas?.[0]?.name || "",
              qty: a.cantidad || 0,
              price: a.precio || 0,
            };
      CAMPOS.forEach((c) => { base[c.key] = a[c.key] === "NA" ? "" : (a[c.key] ?? ""); });
      // primer renglon de cada modelo dentro de la PO: ahi van sus fotos
      const llaveModelo = `${base.po}|${base.idArticulo}|${a.renglon || 1}`;
      base.primeroDelModelo = llaveModelo !== anterior;
      anterior = llaveModelo;
      return base;
    }));
  }

  // Un solo pedido de datos por OC, en paralelo, y todo se junta en la misma tabla.
  async function carga(idProveedor, idOC) {
    setSelectedProveedor(idProveedor);
    setSelectedOC(idOC);
    setFilas([]);
    const objetivo = ordenes.filter((o) =>
      (idOC ? o._id === idOC : true) &&
      (idProveedor ? o.proveedores?.[0]?._id === idProveedor : true)
    );
    if (objetivo.length === 0) return;
    setCargando(true);
    try {
      const ocsPorId = {};
      objetivo.forEach((o) => { ocsPorId[o._id] = o; });
      const respuestas = await Promise.all(
        objetivo.map((o) => axios.get(`${URL_ARTICULOS_OC}/ordenesCompra/${o._id}`, { headers }))
      );
      const todos = respuestas.flatMap((r) => (r.data || []).filter((a) => a.is_active === "Si"));
      pinta(todos, ocsPorId);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Oops...", text: "Could not load the items for these orders" });
    }
    setCargando(false);
  }

  // Checkboxes go out right away; typed text waits until the row settles.
  const cambia = (id, key, valor, inmediato = false) => {
    const actual = filas.find((f) => f._id === id);
    if (!actual) return;
    const fila = { ...actual, [key]: valor };
    setFilas((prev) => prev.map((f) => (f._id === id ? fila : f)));
    programa(fila, inmediato);
  };

  const totalQty = filas.reduce((s, f) => s + (parseFloat(f.qty) || 0), 0);

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_procesos ? (
        <div className="card container col-12">
          <Row>
            <Col md={4}>
              <Button href="/MenuCompras" className="btn btn-danger" size="sm">Back</Button>
            </Col>
            <Col md={5}>
              <Label>Supplier</Label>
              <Input type="select" value={selectedProveedor}
                onChange={(e) => carga(e.target.value, "")}>
                <option value="">Select a supplier</option>
                {Array.from(
                  new Map(
                    ordenes
                      .filter((o) => o.proveedores?.[0])
                      .map((o) => [o.proveedores?.[0]?._id, o.proveedores[0]])
                  ).values()
                )
                  .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                  .map((p) => (
                    <option key={p._id} value={p._id}>{p.nombre_comercial}</option>
                  ))}
              </Input>
              <Input type="select" className="mt-1" value={selectedOC}
                onChange={(e) => carga(selectedProveedor, e.target.value)}>
                <option value="">All purchase orders</option>
                {ordenes
                  .filter((o) => (selectedProveedor ? o.proveedores?.[0]?._id === selectedProveedor : true))
                  .sort((a, b) => (a.idOrdenCompra < b.idOrdenCompra ? 1 : -1))
                  .map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.idOrdenCompra}
                      {a.folioProveedor && a.folioProveedor !== "NA" ? ` (${a.folioProveedor})` : ""}
                      {a.pp && a.pp !== "NA" ? ` - ${a.pp}` : ""}
                      {(modelosPorOC[a._id] || []).length ? ` · ${modelosPorOC[a._id].slice(0, 4).join(", ")}${modelosPorOC[a._id].length > 4 ? "..." : ""}` : ""}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={3} align="right">
              <Label>Total Qty</Label>
              <h5>{new Intl.NumberFormat("en-US").format(totalQty)}</h5>
            </Col>
          </Row>
          <h3 align="center">Production Tracking — Supplier</h3>
          <p align="center" style={{ color: "#64748b", fontSize: "0.8rem", marginBottom: 8 }}>
            Changes are saved automatically. Checkboxes also record the date they were ticked.
            Blue values come from the order, the PO or the shipments; type over them to change them.
          </p>

          {filas.length > 0 ? (
            <div style={{ width: "100%", overflowX: "auto" }}>
              <Table size="sm" striped bordered style={{ whiteSpace: "nowrap", minWidth: "1950px" }}>
                <thead>
                  <tr>
                    <th style={{ position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 2 }}>No.</th>
                    <th>PO</th>
                    <th>Article</th>
                    <th style={{ textAlign: "center" }}>Model photo</th>
                    <th style={{ textAlign: "center" }}>Fit photo</th>
                    <th>Color</th>
                    <th>Size</th>
                    <th align="right">Qty</th>
                    <th align="right">Price</th>
                    {CAMPOS.map((c) => (<th key={c.key} style={{ minWidth: c.w }}>{c.label}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f._id} style={{ background: fondoFila(estado[f._id]) }}>
                      <td style={{ position: "sticky", left: 0, backgroundColor: fondoFila(estado[f._id]) || "#fff", zIndex: 1, fontWeight: 600 }}>
                        {f.no}
                        {estado[f._id] ? (
                          <small style={{ display: "block", fontSize: "0.62rem", fontWeight: 400, color: COLOR_SELLO[estado[f._id]] }}>
                            {SELLO_EN[estado[f._id]]}
                          </small>
                        ) : null}
                      </td>
                      <td>{f.po}</td>
                      <td style={{ fontWeight: f.primeroDelModelo ? 600 : 400 }}>{f.articulo}</td>
                      <td style={{ textAlign: "center" }}>
                        {f.primeroDelModelo && f.idArticulo ? (
                          <FotoArticulo idArticulo={f.idArticulo} url={f.urlFoto} tipo="modelo" tam={48} ingles
                            onSubida={(url) => setFilas((prev) => prev.map((x) => (x.idArticulo === f.idArticulo ? { ...x, urlFoto: url } : x)))} />
                        ) : null}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {f.primeroDelModelo && f.idArticulo ? (
                          <FotoArticulo idArticulo={f.idArticulo} url={f.urlFotoFit} tipo="fit" tam={48} ingles
                            onSubida={(url) => setFilas((prev) => prev.map((x) => (x.idArticulo === f.idArticulo ? { ...x, urlFotoFit: url } : x)))} />
                        ) : null}
                      </td>
                      <td>{f.color}</td>
                      <td>{f.talla}</td>
                      <td align="right">{new Intl.NumberFormat("en-US").format(f.qty)}</td>
                      <td align="right">
                        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(f.price)}
                      </td>
                      {CAMPOS.map((c) => (
                        <td key={c.key} style={{ minWidth: c.w, textAlign: c.tipo === "check" ? "center" : undefined }}>
                          {c.tipo === "check" ? (
                            <CheckFecha valor={f[c.key]} auto={autos[f._id]?.[c.key]} onChange={(v) => cambia(f._id, c.key, v, true)} />
                          ) : (
                            <CampoAuto tipo={c.tipo || "text"} valor={f[c.key]} auto={autos[f._id]?.[c.key]} ingles
                              onChange={(v) => cambia(f._id, c.key, v)} />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : cargando ? (
            <h6 align="center">Loading...</h6>
          ) : selectedProveedor ? (
            <h6 align="center">No items for this supplier.</h6>
          ) : (
            <h6 align="center">Select a supplier to see all of its purchase orders.</h6>
          )}
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default SeguimientoChina;
